import publicRoutes from './seo/routes.json';

/** Deliberately allowlisted analytics: never pass API objects, query text or user data. */
export const GA_ID = 'G-CL29K0CHZ8';
export const METRIKA_ID = 113529125;
export const ANALYTICS_ORIGIN = 'https://glauncher.kz';
export const CONSENT_KEY = 'gl.analytics.consent.v1';
const PURCHASES_KEY = 'gl.analytics.purchases.v1';
const MAX_QUEUE = 30;
const QUEUE_TTL = 60_000;
export type AnalyticsConsent = 'granted' | 'denied' | null;
type Parameters = Record<string, string | number | boolean | object>;
type AuthState = 'pending' | 'allowed' | 'excluded';
type AnalyticsEvent = { name: string; params: Parameters; path: string; time: number; transaction?: string };

declare global {
  interface Window {
    __GL_PRERENDER__?: boolean;
    dataLayer?: unknown[];
    glEcommerceLayer?: object[];
    gtag?: (...args: unknown[]) => void;
    ym?: ((...args: unknown[]) => void) & { a?: IArguments[]; l?: number };
    'ga-disable-G-CL29K0CHZ8'?: boolean;
  }
}

const pages: Record<string, string> = {
  ...Object.fromEntries(publicRoutes.map(route => [route.path, route.title])),
  '/lots': 'Поиск лотов — G-Launcher', '/suppliers': 'Поставщики — G-Launcher',
  '/payment/success': 'Результат оплаты — G-Launcher', '/payment/failure': 'Результат оплаты — G-Launcher',
};

export function analyticsPage(value: string): { url: string; title: string } | null {
  try {
    const input = new URL(value, ANALYTICS_ORIGIN);
    let path = input.pathname.replace(/\/+$/, '') || '/';
    // Detail URLs can contain an IIN, name or an arbitrary slug. Only their type is measured.
    if (/^\/lots\/[^/]+$/.test(path)) path = '/lots/detail';
    else if (/^\/suppliers\/[^/]+$/.test(path)) path = '/suppliers/detail';
    else if (/^\/news\/[^/]+$/.test(path)) path = '/news/article';
    const title = pages[path] || ({ '/lots/detail': pages['/lots'], '/suppliers/detail': pages['/suppliers'], '/news/article': pages['/news'] })[path];
    if (!title) return null;
    const result = new URL(path, ANALYTICS_ORIGIN);
    // Campaign labels must be non-personal slugs. Search terms/content and click IDs are excluded.
    for (const key of ['utm_source', 'utm_medium', 'utm_campaign']) {
      const label = input.searchParams.get(key);
      if (label && /^[a-z][a-z0-9_-]{0,63}$/i.test(label) && !/\d{7}/.test(label)) result.searchParams.set(key, label);
    }
    return { url: result.href, title };
  } catch { return null; }
}

export function analyticsReferrer(value: string): string {
  if (!value) return '';
  try {
    const url = new URL(value);
    if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password) return '';
    if (['glauncher.kz', 'www.glauncher.kz'].includes(url.hostname)) return analyticsPage(url.pathname)?.url || '';
    return url.origin + '/';
  } catch { return ''; }
}

export function analyticsEnabled(): boolean {
  return typeof window !== 'undefined' && !window.__GL_PRERENDER__ &&
    process.env.NODE_ENV === 'production' && process.env.REACT_APP_ANALYTICS_ENABLED === 'true' &&
    window.location.protocol === 'https:' && ['glauncher.kz', 'www.glauncher.kz'].includes(window.location.hostname);
}

export function readAnalyticsConsent(): AnalyticsConsent {
  try { const value = localStorage.getItem(CONSENT_KEY); return value === 'granted' || value === 'denied' ? value : null; }
  catch { return null; }
}

export interface AnalyticsTransport {
  start: (page: { url: string; title: string }, referrer: string) => void;
  stop: () => void;
  context: (page: { url: string; title: string }, referrer: string) => void;
  page: (page: { url: string; title: string }, referrer: string) => void;
  event: (name: string, params: Parameters) => void;
}

function addScript(id: string, src: string, onload: () => void) {
  if (document.getElementById(id)) return;
  const script = document.createElement('script');
  script.id = id; script.async = true; script.src = src; script.referrerPolicy = 'no-referrer';
  script.onload = onload;
  document.head.appendChild(script);
}

let metrikaHasPage = false;
let googleLoaded = false;
let metrikaLoaded = false;
const ga4Enabled = () => process.env.REACT_APP_GA4_ENABLED === 'true';
let metrikaPending: Array<{ name: string; params: Parameters }> = [];
function metrikaEvent(name: string, params: Parameters) {
  // Metrica derives a goal's referrer from its last hit. Before the first hit it
  // falls back to location.href, which may contain a payment/OAuth token.
  if (!metrikaHasPage) { metrikaPending.push({ name, params }); metrikaPending = metrikaPending.slice(-MAX_QUEUE); return; }
  window.ym?.(METRIKA_ID, 'reachGoal', name, params);
  if (name === 'purchase') window.glEcommerceLayer?.push({ ecommerce: { currencyCode: 'KZT', purchase: {
    actionField: { id: params.transaction_id, revenue: params.value },
    products: [{ id: params.purchase_kind, name: params.purchase_kind === 'subscription' ? 'Подписка G-Launcher' : 'Услуги G-Launcher', price: params.value, quantity: 1 }],
  } } });
}

const browserTransport: AnalyticsTransport = {
  start(page, referrer) {
    window['ga-disable-G-CL29K0CHZ8'] = !ga4Enabled();
    if (ga4Enabled()) {
    window.dataLayer = window.dataLayer || [];
    // gtag expects an Arguments object, not an array.
    window.gtag = window.gtag || function () { window.dataLayer!.push(arguments); };
    window.gtag('consent', 'default', { analytics_storage: 'granted', ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' });
    window.gtag('consent', 'update', { analytics_storage: 'granted', ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' });
    window.gtag('js', new Date());
    window.gtag('config', GA_ID, { send_page_view: false, page_location: page.url, page_referrer: referrer,
      page_title: page.title, allow_google_signals: false, allow_ad_personalization_signals: false });
      addScript('gl-google-analytics', `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`, () => { googleLoaded = true; });
    }
    window.glEcommerceLayer = window.glEcommerceLayer || [];
    if (!window.ym) {
      const ym: NonNullable<Window['ym']> = function () { (ym.a = ym.a || []).push(arguments); };
      ym.l = Date.now(); window.ym = ym;
    }
    window.ym(METRIKA_ID, 'init', { defer: true, ssr: true, webvisor: false, clickmap: false,
      trackLinks: false, trackHash: false, accurateTrackBounce: false, sendTitle: false,
      disableFormAnalytics: true, ecommerce: 'glEcommerceLayer', url: page.url, referrer: referrer || ANALYTICS_ORIGIN + '/' });
    addScript('gl-yandex-metrika', `https://mc.yandex.ru/metrika/tag.js?id=${METRIKA_ID}`, () => { metrikaLoaded = true; });
  },
  stop() {
    metrikaHasPage = false; metrikaPending = [];
    window['ga-disable-G-CL29K0CHZ8'] = true;
    window.gtag?.('consent', 'update', { analytics_storage: 'denied' });
    window.ym?.(METRIKA_ID, 'destruct');
    // Also discard stubs if consent/auth changed while a vendor script was downloading.
    if (!metrikaLoaded && window.ym?.a) window.ym.a.length = 0;
    if (!googleLoaded && window.dataLayer) window.dataLayer.length = 0;
    if (window.glEcommerceLayer) window.glEcommerceLayer.length = 0;
  },
  context(page, referrer) {
    window.gtag?.('set', { page_location: page.url, page_referrer: referrer, page_title: page.title });
  },
  page(page, referrer) {
    window.gtag?.('event', 'page_view', { send_to: GA_ID, page_location: page.url, page_referrer: referrer, page_title: page.title });
    window.ym?.(METRIKA_ID, 'hit', page.url, { referer: referrer || ANALYTICS_ORIGIN + '/', title: page.title });
    metrikaHasPage = true;
    metrikaPending.splice(0).forEach(event => metrikaEvent(event.name, event.params));
  },
  event(name, params) {
    window.gtag?.('event', name, { send_to: GA_ID, ...params });
    metrikaEvent(name, params);
  },
};

/** A bounded, consent-only queue waits for authoritative auth instead of treating null as a guest. */
export class AnalyticsClient {
  private consent: AnalyticsConsent = null;
  private auth: AuthState = 'pending';
  private started = false;
  private lastView: string | null = null;
  private referrer = '';
  private queue: AnalyticsEvent[] = [];
  private purchases = new Set<string>();
  constructor(private transport: AnalyticsTransport, private enabled: () => boolean = analyticsEnabled,
    private now: () => number = Date.now, private sessionAllowed: () => boolean = () => true) {}

  update(consent: AnalyticsConsent, auth: AuthState, location: string, referrer = '') {
    this.consent = consent; this.auth = auth;
    if (!this.enabled() || consent !== 'granted' || auth === 'excluded') {
      this.queue = []; this.lastView = null; this.referrer = '';
      if (this.started) this.transport.stop();
      this.started = false; return;
    }
    if (auth === 'pending' || !this.sessionAllowed()) {
      if (this.started) this.transport.stop();
      this.started = false; return;
    }
    const page = analyticsPage(location);
    if (page) {
      this.ensureStarted(page, analyticsReferrer(referrer));
      if (this.lastView !== page.url) {
        const previous = this.referrer || analyticsReferrer(referrer);
        this.transport.context(page, previous); this.transport.page(page, previous);
        this.lastView = page.url; this.referrer = page.url;
      }
    } else {
      this.lastView = null;
      if (this.started) this.transport.context(analyticsPage('/')!, '');
    }
    const pending = this.queue.splice(0);
    pending.filter(event => this.now() - event.time < QUEUE_TTL).forEach(event => {
      try { this.send(event); } catch { /* A blocked vendor must not interrupt auth/bootstrap. */ }
    });
  }

  private ensureStarted(page: { url: string; title: string }, referrer: string) {
    if (!this.started) { this.transport.start(page, referrer); this.started = true; }
  }

  private send(event: AnalyticsEvent) {
    if (!this.enabled() || this.consent !== 'granted' || this.auth !== 'allowed' || !this.sessionAllowed()) return;
    if (event.transaction && this.alreadyPurchased(event.transaction)) return;
    const page = analyticsPage(event.path)!;
    this.ensureStarted(page, '');
    this.transport.context(page, '');
    this.transport.event(event.name, { ...event.params, page_location: page.url, page_referrer: '', page_title: page.title });
    if (event.transaction) this.rememberPurchase(event.transaction);
  }

  private record(name: string, params: Parameters, path: string, transaction?: string) {
    if (!this.enabled() || this.consent !== 'granted' || this.auth === 'excluded') return;
    const event = { name, params, path, transaction, time: this.now() };
    if (this.auth === 'pending' || !this.sessionAllowed()) {
      if (!transaction || !this.queue.some(item => item.transaction === transaction)) this.queue.push(event);
      this.queue = this.queue.filter(item => this.now() - item.time < QUEUE_TTL).slice(-MAX_QUEUE); return;
    }
    try { this.send(event); } catch { /* Analytics must never change an application's success/failure result. */ }
  }

  signUp(method: 'email' | 'google') { this.record('sign_up', { method }, '/'); }
  trialStarted() { this.record('trial_started', { duration_days: 7 }, '/lots'); }
  previewSearch(service: 'lots' | 'suppliers', resultCount: number) {
    this.record('preview_search', { search_service: service, result_count: Math.max(0, Math.min(10, Math.trunc(resultCount))) }, `/${service}`);
  }
  search(service: 'lots' | 'suppliers', resultCount: number) {
    this.record('search', { search_service: service, result_count: Math.max(0, Math.min(100, Math.trunc(resultCount))) }, `/${service}`);
  }
  searchSubscription(site: boolean, telegram: boolean) { this.record('search_subscription_created', { channel_site: !!site, channel_telegram: !!telegram }, '/lots'); }
  trackingAdded(source: 'lot' | 'announcement' | 'manual') { this.record('tracking_added', { source }, '/lots'); }
  promotion(kind: 'impression' | 'click', id: string, revision: number, placement: string, path: string) {
    if (!/^[a-f0-9]{24}$/.test(id) || !Number.isSafeInteger(revision) || revision < 1 ||
      !['home_lower', 'news_top', 'article_bottom'].includes(placement) || !analyticsPage(path)) return;
    this.record(kind === 'impression' ? 'view_promotion' : 'select_promotion', {
      promotion_id: id, creative_slot: placement, creative_revision: revision,
    }, path);
  }
  purchase(order: { invoice_id: string; status: string; amount: number; currency: string; kind: string; analytics_eligible?: boolean }) {
    if (order.analytics_eligible !== true || order.status !== 'paid' || order.currency !== 'KZT' ||
      !Number.isFinite(order.amount) || order.amount <= 0 || !/^[a-zA-Z0-9_-]{1,100}$/.test(order.invoice_id) ||
      !['purchase', 'subscription'].includes(order.kind)) return;
    this.record('purchase', { transaction_id: order.invoice_id, value: order.amount, currency: 'KZT', purchase_kind: order.kind,
      items: [{ item_id: order.kind, item_name: order.kind === 'subscription' ? 'Подписка G-Launcher' : 'Услуги G-Launcher', price: order.amount, quantity: 1 }],
    }, '/plans', order.invoice_id);
  }
  private alreadyPurchased(id: string): boolean {
    if (this.purchases.has(id)) return true;
    try {
      const entries: unknown = JSON.parse(localStorage.getItem(PURCHASES_KEY) || '[]');
      return Array.isArray(entries) && entries.some(entry => entry?.id === id);
    } catch { return false; }
  }
  private rememberPurchase(id: string) {
    this.purchases.add(id);
    try {
      const stored = JSON.parse(localStorage.getItem(PURCHASES_KEY) || '[]');
      const entries = Array.isArray(stored) ? stored.filter(entry => typeof entry?.id === 'string' && this.now() - entry.time < 400 * 86400000) : [];
      localStorage.setItem(PURCHASES_KEY, JSON.stringify([...entries, { id, time: this.now() }].slice(-1000)));
    } catch { /* In-memory deduplication still works when browser storage is unavailable. */ }
  }
}

let expectedToken: string | null = null;
function currentToken(): string | null {
  try { return localStorage.getItem('access_token') || sessionStorage.getItem('access_token'); } catch { return null; }
}
export const analytics = new AnalyticsClient(browserTransport, analyticsEnabled, Date.now, () => currentToken() === expectedToken);

export function updateAnalyticsAuth(consent: AnalyticsConsent, user: { role: string; email: string } | null, loading: boolean, location: string) {
  const token = currentToken();
  let auth: AuthState = loading || (token && !user) ? 'pending' : 'allowed';
  if (user && user.role !== 'user') auth = 'excluded';
  if (token && user && auth === 'allowed') {
    try {
      const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      if (payload.sub !== user.email) auth = 'pending';
    } catch { auth = 'pending'; }
  }
  expectedToken = token;
  try { analytics.update(consent, auth, location, document.referrer); } catch { /* Analytics cannot interrupt navigation. */ }
  return auth;
}

export function setAnalyticsConsent(consent: Exclude<AnalyticsConsent, null>) {
  try { localStorage.setItem(CONSENT_KEY, consent); } catch { /* Choice still takes effect in this tab. */ }
  window.dispatchEvent(new CustomEvent('gl:analytics-consent', { detail: consent }));
}
